'use client';

import { useState } from 'react';
import { useCartStore } from '@/lib/cartStore';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ShoppingCart, Check } from 'lucide-react';
import { AddToCartDialog } from '@/components/add-to-cart-dialog';
import { useExperienceCartDrawerStore } from '@/lib/experienceCartDrawerStore';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';

type AddToCartButtonProps = {
  product: {
    id: string;
    name: string;
    price: number;
    imageUrl?: string;
    isAvailable?: boolean;
  };
  variantId?: string;
  variantDetails?: {
    size?: string;
    color?: string;
    dosage?: string;
    form?: string;
    [key: string]: string | undefined;
  };
  foodSelections?: {
    optionGroups: Array<{ groupId: string; optionIds: string[] }>;
    additionals: Array<{ additionalId: string; quantity: number }>;
  };
  storeId: string;
  publicStoreId?: string;
  locale?: string;
  label: string;
  unavailableLabel: string;
  successMessage: string;
  showDialog?: boolean;
  /** When false, skips the success toast (non-dialog flow only). Defaults to true. */
  showSuccessToast?: boolean;
  dialogTranslations?: {
    title: string;
    goToCart: string;
    continueShopping: string;
  };
  /** When true, opens the experience cart drawer after add (overrides toast). */
  openExperienceCartDrawer?: boolean;
  disabled?: boolean;
  onAdd?: () => void; // Optional callback when item is added
  onDialogClose?: () => void; // Optional callback when confirmation dialog closes
  className?: string;
};

export function AddToCartButton({
  product,
  variantId,
  variantDetails,
  foodSelections,
  storeId,
  publicStoreId,
  locale,
  label,
  unavailableLabel,
  successMessage,
  showDialog = false,
  showSuccessToast = true,
  openExperienceCartDrawer = false,
  dialogTranslations,
  disabled: externalDisabled = false,
  onAdd,
  onDialogClose,
  className,
}: AddToCartButtonProps) {
  const t = useTranslations('product');
  const experienceCartDrawerEnabled = useExperienceCartDrawerStore((state) => state.enabled);
  const openExperienceCartDrawerFromStore = useExperienceCartDrawerStore((state) => state.openDrawer);
  const [isAdding, setIsAdding] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const [showDialogState, setShowDialogState] = useState(false);
  const addItem = useCartStore((state) => state.addItem);
  const setStoreId = useCartStore((state) => state.setStoreId);

  const handleAddToCart = () => {
    // Ensure store is set
    setStoreId(storeId);

    // Add item to cart
    addItem({
      productId: product.id,
      variantId,
      name: product.name,
      price: product.price,
      imageUrl: product.imageUrl,
      variantDetails,
      foodSelections,
    }, 1);

    // Show "Added!" animation
    setIsAdding(true);
    setJustAdded(true);

    if (showDialog && storeId && locale && dialogTranslations) {
      // Show animation first, then open dialog after delay
      setTimeout(() => {
        setIsAdding(false);
        setJustAdded(false);
        setShowDialogState(true);
        // Call onAdd callback if provided (e.g., to close parent dialog)
        if (onAdd) {
          onAdd();
        }
      }, 800);
    } else {
      const shouldOpenExperienceCartDrawer =
        openExperienceCartDrawer || experienceCartDrawerEnabled;

      if (shouldOpenExperienceCartDrawer) {
        setTimeout(() => {
          setIsAdding(false);
          setJustAdded(false);
          openExperienceCartDrawerFromStore();
          if (onAdd) {
            onAdd();
          }
        }, 800);
      } else if (showSuccessToast) {
        toast.success(successMessage, {
          description: product.name,
          duration: 3000,
        });

        setTimeout(() => {
          setIsAdding(false);
          setJustAdded(false);
          // Call onAdd callback if provided
          if (onAdd) {
            onAdd();
          }
        }, 1500);
      } else {
        setTimeout(() => {
          setIsAdding(false);
          setJustAdded(false);
          if (onAdd) {
            onAdd();
          }
        }, 1500);
      }
    }
  };

  const isDisabled = product.isAvailable === false || isAdding || externalDisabled;

  return (
    <>
      <AddToCartDialog
        open={showDialogState}
        onOpenChange={(isOpen) => {
          setShowDialogState(isOpen);
          // Call onDialogClose when dialog closes
          if (!isOpen && onDialogClose) {
            onDialogClose();
          }
        }}
        productName={product.name}
        storeId={storeId}
        publicStoreId={publicStoreId}
        locale={locale || ''}
        translations={dialogTranslations || { title: '', goToCart: '', continueShopping: '' }}
      />
      <Button
        className={cn('w-full text-lg py-6 rounded-full', className)}
        disabled={isDisabled}
        onClick={handleAddToCart}
      >
        {justAdded ? (
          <>
            <Check className="w-5 h-5 mr-2" />
            {successMessage}
          </>
        ) : (
          <>
            <ShoppingCart className="w-5 h-5 mr-2" />
            {product.isAvailable === false ? unavailableLabel : label}
          </>
        )}
      </Button>
    </>
  );
}
